import type {
  LoyaltyAdjustInput,
  LoyaltyBlockInput,
  LoyaltyCustomerStatus,
  LoyaltyEntry,
  LoyaltyInvoiceInfo,
  LoyaltyMember,
  LoyaltyMemberListItem,
  LoyaltyProgram,
  LoyaltyProgramUpdate,
  LoyaltyRule,
  LoyaltyRuleInput,
  LoyaltySummary,
  LoyaltyTier,
  LoyaltyTierInput,
  Page,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { EntryListParams, MemberListParams } from './search'

export const loyaltyKeys = {
  all: ['loyalty'] as const,
  program: ['loyalty', 'program'] as const,
  tiers: ['loyalty', 'tiers'] as const,
  rules: ['loyalty', 'rules'] as const,
  members: ['loyalty', 'members'] as const,
  memberList: (query: MemberListParams) => [...loyaltyKeys.members, 'list', query] as const,
  member: (id: string) => [...loyaltyKeys.members, 'detail', id] as const,
  entries: ['loyalty', 'entries'] as const,
  entryList: (query: EntryListParams) => [...loyaltyKeys.entries, 'list', query] as const,
  customers: ['loyalty', 'customers'] as const,
  customer: (customerId: string) => [...loyaltyKeys.customers, customerId] as const,
  invoice: (invoiceId: string) => ['loyalty', 'invoices', invoiceId] as const,
  summary: (from: string, to: string) => ['loyalty', 'summary', from, to] as const,
}

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── programme, tiers, rules ──────────────────────────────────────────────────

export const loyaltyProgramQueryOptions = queryOptions({
  queryKey: loyaltyKeys.program,
  queryFn: ({ signal }) => api.get<LoyaltyProgram>('/loyalty/program', { signal }),
  staleTime: 60_000,
})

export const loyaltyTiersQueryOptions = queryOptions({
  queryKey: loyaltyKeys.tiers,
  queryFn: ({ signal }) => api.get<LoyaltyTier[]>('/loyalty/tiers', { signal }),
  staleTime: 60_000,
})

export const loyaltyRulesQueryOptions = queryOptions({
  queryKey: loyaltyKeys.rules,
  queryFn: ({ signal }) => api.get<LoyaltyRule[]>('/loyalty/rules', { signal }),
})

/**
 * The programme's terms change what every balance is worth and how members earn: everything loyalty
 * shows is refreshed (the overview, members, statements, the customer pages' summaries).
 */
function invalidateLoyalty(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: loyaltyKeys.all })
}

export function useSaveLoyaltyProgram() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LoyaltyProgramUpdate) => api.put<LoyaltyProgram>('/loyalty/program', input),
    onSuccess: (program) => {
      queryClient.setQueryData(loyaltyKeys.program, program)
      return queryClient.invalidateQueries({
        queryKey: loyaltyKeys.all,
        predicate: (q) => q.queryKey[1] !== 'program',
      })
    },
  })
}

export function useSaveLoyaltyTier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: LoyaltyTierInput & { version?: number } }) =>
      id
        ? api.put<LoyaltyTier>(path('/loyalty/tiers', id), input)
        : api.post<LoyaltyTier>('/loyalty/tiers', input),
    // A tier's threshold or multiplier moves members between tiers.
    onSuccess: () => invalidateLoyalty(queryClient),
  })
}

export function useDeleteLoyaltyTier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(path('/loyalty/tiers', id)),
    onSuccess: () => invalidateLoyalty(queryClient),
  })
}

export function useSaveLoyaltyRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, rule, version }: { id: string | null; rule: LoyaltyRuleInput; version?: number }) =>
      id
        ? api.put<LoyaltyRule>(path('/loyalty/rules', id), { rule, version })
        : api.post<LoyaltyRule>('/loyalty/rules', rule),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: loyaltyKeys.rules }),
  })
}

export function useDeleteLoyaltyRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(path('/loyalty/rules', id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: loyaltyKeys.rules }),
  })
}

// ── members ──────────────────────────────────────────────────────────────────

export function loyaltyMemberListQueryOptions(query: MemberListParams) {
  return queryOptions({
    queryKey: loyaltyKeys.memberList(query),
    queryFn: ({ signal }) =>
      api.get<Page<LoyaltyMemberListItem>>('/loyalty/members', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          status: query.status,
          balance: query.balance,
          tierId: query.tierId,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function loyaltyMemberQueryOptions(id: string) {
  return queryOptions({
    queryKey: loyaltyKeys.member(id),
    queryFn: ({ signal }) => api.get<LoyaltyMember>(path('/loyalty/members', id), { signal }),
  })
}

/** GET /loyalty/customers/:id: whether a customer is a member, and their points if so. */
export function loyaltyCustomerQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: loyaltyKeys.customer(customerId),
    queryFn: ({ signal }) =>
      api.get<LoyaltyCustomerStatus>(path('/loyalty/customers', customerId), { signal }),
  })
}

/** GET /loyalty/invoices/:id: what loyalty did on one invoice (404 when nothing). */
export function loyaltyInvoiceQueryOptions(invoiceId: string) {
  return queryOptions({
    queryKey: loyaltyKeys.invoice(invoiceId),
    queryFn: ({ signal }) => api.get<LoyaltyInvoiceInfo>(path('/loyalty/invoices', invoiceId), { signal }),
  })
}

/**
 * A member changed (joined, blocked, points added or taken): its page takes the answer, and the lists,
 * the statement, the customer pages and the overview refresh.
 */
function useMemberMutation<TInput>(request: (input: TInput) => Promise<LoyaltyMember>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (member) => {
      queryClient.setQueryData(loyaltyKeys.member(member.id), member)
      return queryClient.invalidateQueries({
        queryKey: loyaltyKeys.all,
        // The tiers count their members; the programme and the bonus rules stay as they are.
        predicate: (q) =>
          !(q.queryKey[1] === 'members' && q.queryKey[2] === 'detail' && q.queryKey[3] === member.id) &&
          q.queryKey[1] !== 'program' &&
          q.queryKey[1] !== 'rules',
      })
    },
  })
}

export function useEnrollMember() {
  return useMemberMutation((customerId: string) =>
    api.post<LoyaltyMember>('/loyalty/members', { customerId }),
  )
}

export function useBlockMember(id: string) {
  return useMemberMutation((input: LoyaltyBlockInput) =>
    api.post<LoyaltyMember>(path('/loyalty/members', id, 'block'), input),
  )
}

export function useUnblockMember(id: string) {
  return useMemberMutation((version: number) =>
    api.post<LoyaltyMember>(path('/loyalty/members', id, 'unblock'), { version }),
  )
}

export function useAdjustPoints(id: string) {
  return useMemberMutation((input: LoyaltyAdjustInput) =>
    api.post<LoyaltyMember>(path('/loyalty/members', id, 'adjustments'), input),
  )
}

// ── the ledger and the overview ──────────────────────────────────────────────

export function loyaltyEntryListQueryOptions(query: EntryListParams) {
  return queryOptions({
    queryKey: loyaltyKeys.entryList(query),
    queryFn: ({ signal }) =>
      api.get<Page<LoyaltyEntry>>('/loyalty/entries', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          memberId: query.memberId,
          q: query.q,
          kind: query.kind,
          from: query.from,
          to: query.to,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

export function loyaltySummaryQueryOptions(from: string, to: string) {
  return queryOptions({
    queryKey: loyaltyKeys.summary(from, to),
    queryFn: ({ signal }) => api.get<LoyaltySummary>('/loyalty/summary', { signal, query: { from, to } }),
    placeholderData: keepPreviousData,
  })
}
